/**
 * Dashboard Analytics Service
 * ============================
 * Client for fetching Dashboard Analytics data and managing trading goals.
 */

import { getApiUrl } from '../api/config';

const getBaseUrl = () => `${getApiUrl()}/dashboard`;

/**
 * Fetch Dashboard Analytics data (pattern recognition, goal progress, plan adherence).
 * @param {boolean} refresh - If true, forces recomputation. Default false.
 */
export async function getDashboardAnalytics(refresh = false) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/analytics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ refresh }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to fetch Dashboard Analytics: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch user's active trading goals.
 */
export async function getGoals() {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/goals`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to fetch goals');
  }

  return response.json();
}

/**
 * Create or upsert a trading goal.
 * @param {{ goal_type: string, target_value: number }} goal
 */
export async function createGoal(goal) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/goals`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(goal),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create goal');
  }

  return response.json();
}

/**
 * Update a trading goal.
 * @param {string} id - Goal ID
 * @param {{ target_value?: number, is_active?: boolean }} updates
 */
export async function updateGoal(id, updates) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/goals/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to update goal');
  }

  return response.json();
}

/**
 * Delete a trading goal.
 * @param {string} id - Goal ID
 */
export async function deleteGoal(id) {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${getBaseUrl()}/goals/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to delete goal');
  }

  return response.json();
}
